/**
 * useCountUp.ts — Custom hook that animates a number from 0 to `target`
 * in ~600 ms using requestAnimationFrame with an ease-out cubic curve.
 *
 * Animation only runs when `active` is true; otherwise the current value
 * is set immediately to `target` (or 0 if target is undefined).
 */

import { useEffect, useRef, useState } from 'react';

export function useCountUp(target: number | undefined, active: boolean): number {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active || target == null) {
      setCurrent(target ?? 0);
      return;
    }

    const duration = 600;
    startRef.current = null;

    const tick = (ts: number) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, active]);

  return current;
}
